import type { DefaultImagePickerProps } from '../../default-image-picker';
import { type Thumbnail, ThumbnailType, type UnsplashResult } from '../../types';
import { isUnsplashImage } from '../../utils/unsplash-image';

const externalSiteMatcher =
	/^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;

const createCustomImageThumbnail = (imageId: string): Thumbnail => ({
	type: ThumbnailType.CUSTOM_IMAGE,
	image: { imageId },
});

export const createUnsplashDefaultImageThumbnail = (image?: UnsplashResult): Thumbnail => ({
	type: ThumbnailType.UNSPLASH_DEFAULT_IMAGE,
	image,
});

const createUnsplashImageThumbnail = (url: string, altText?: string): Thumbnail => ({
	type: ThumbnailType.UNSPLASH_IMAGE,
	image: { fullSizeImageUrl: url, altDescription: altText },
});

const createExternalSiteImageThumbnail = (url: string): Thumbnail => ({
	type: ThumbnailType.EXTERNAL_SITE_IMAGE,
	image: url,
});

const initialUnsplashResultsIncludes = ({
	image,
	initialUnsplashResults,
	isNoImageAllowed,
}: Partial<
	Pick<DefaultImagePickerProps, 'initialUnsplashResults' | 'isNoImageAllowed'> & { image: string }
>) => {
	if (!initialUnsplashResults?.length) {
		return false;
	}
	const adjustedUnsplashResults = isNoImageAllowed
		? initialUnsplashResults.slice(2)
		: initialUnsplashResults.slice(1);

	return adjustedUnsplashResults
		.map((result: UnsplashResult) => result.fullSizeImageUrl)
		.includes(image || '');
};

type RequiredFields<T, K extends keyof T> = T & Required<Pick<T, K>>;
type HandleUnsplashSelectedImageProps = RequiredFields<GetFirstImageProps, 'selectedImageUrl'>;

const getFirstImageWhenSelectedImageIsUnsplash = ({
	selectedImageUrl,
	firstImage,
	initialUnsplashResults,
	isNoImageAllowed,
	previewUploadedImageId,
	initialUploadedImageId,
	imageAltText,
}: HandleUnsplashSelectedImageProps): Thumbnail => {
	const isSelectedUnsplashInInitialResults = (image = selectedImageUrl) =>
		initialUnsplashResultsIncludes({
			image,
			initialUnsplashResults,
			isNoImageAllowed,
		});

	// if selected unsplash image is not in initial results array, then it should always be in the
	// first thumbnail
	if (!isSelectedUnsplashInInitialResults()) {
		return createUnsplashImageThumbnail(selectedImageUrl, imageAltText);
	}

	// TODO: this code can be removed when UnsplashSearch is replaced by MediaPickerContent
	const currentFirstImageUnsplash =
		firstImage?.current?.image &&
		typeof firstImage?.current?.image !== 'string' &&
		'fullSizeImageUrl' in firstImage.current.image &&
		isUnsplashImage(firstImage.current.image.fullSizeImageUrl) &&
		firstImage?.current?.image?.fullSizeImageUrl;

	if (currentFirstImageUnsplash && selectedImageUrl !== currentFirstImageUnsplash) {
		if (
			!isSelectedUnsplashInInitialResults(currentFirstImageUnsplash) &&
			isSelectedUnsplashInInitialResults()
		) {
			return createUnsplashImageThumbnail(currentFirstImageUnsplash, imageAltText);
		}

		if (!isSelectedUnsplashInInitialResults()) {
			return createUnsplashImageThumbnail(selectedImageUrl, imageAltText);
		}
	}

	if (previewUploadedImageId && firstImage.current?.image === previewUploadedImageId) {
		if (isSelectedUnsplashInInitialResults()) {
			return firstImage.current;
		}

		if (!isSelectedUnsplashInInitialResults()) {
			return createUnsplashImageThumbnail(selectedImageUrl, imageAltText);
		}
	}

	if (
		firstImage?.current?.image &&
		typeof firstImage.current.image !== 'string' &&
		'imageId' in firstImage.current.image &&
		isSelectedUnsplashInInitialResults()
	) {
		return firstImage.current;
	}

	if (isSelectedUnsplashInInitialResults() && previewUploadedImageId && initialUploadedImageId) {
		return createCustomImageThumbnail(previewUploadedImageId);
	}

	if (isSelectedUnsplashInInitialResults() && previewUploadedImageId) {
		return createCustomImageThumbnail(previewUploadedImageId);
	}

	if (isSelectedUnsplashInInitialResults() && initialUnsplashResults?.length) {
		return createUnsplashDefaultImageThumbnail(initialUnsplashResults[0]);
	}

	return createUnsplashDefaultImageThumbnail();
};

type GetFirstImageProps = Pick<
	DefaultImagePickerProps,
	| 'selectedImageUrl'
	| 'initialUnsplashResults'
	| 'isNoImageAllowed'
	| 'previewUploadedImageId'
	| 'initialUploadedImageId'
	| 'imageAltText'
> & {
	firstImage: {
		current?: Thumbnail;
	};
};

export const getFirstImage = ({
	selectedImageUrl,
	initialUnsplashResults,
	isNoImageAllowed,
	firstImage,
	previewUploadedImageId,
	initialUploadedImageId,
	imageAltText,
}: GetFirstImageProps): Thumbnail => {
	if (selectedImageUrl) {
		if (isUnsplashImage(selectedImageUrl)) {
			return getFirstImageWhenSelectedImageIsUnsplash({
				selectedImageUrl,
				firstImage,
				initialUnsplashResults,
				isNoImageAllowed,
				previewUploadedImageId,
				initialUploadedImageId,
				imageAltText,
			});
		} else {
			if (externalSiteMatcher.test(selectedImageUrl)) {
				return createExternalSiteImageThumbnail(selectedImageUrl);
			}
			return createCustomImageThumbnail(selectedImageUrl);
		}
	}

	if (initialUploadedImageId) {
		return createCustomImageThumbnail(initialUploadedImageId);
	}

	if (initialUnsplashResults?.length) {
		return createUnsplashDefaultImageThumbnail(initialUnsplashResults[0]);
	}

	return createUnsplashDefaultImageThumbnail();
};
